<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import MetricHint from './MetricHint.vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  tooltip: {
    type: String,
    required: true,
  },
  conversations: {
    type: Array,
    default: () => [],
  },
  emptyLabel: {
    type: String,
    required: true,
  },
  totalCount: {
    type: Number,
    default: 0,
  },
});

const { t } = useI18n();
const emptyValue = computed(() => t('COMPANY_REPORTS.EMPTY_VALUE'));
const rows = computed(() => props.conversations || []);
const showsLatestOnly = computed(
  () => props.totalCount > rows.value.length && rows.value.length > 0
);

const formatTimestamp = value =>
  value ? shortTimestamp(dynamicTime(value), true) : emptyValue.value;

const conversationRoute = conversation => ({
  name: 'inbox_conversation',
  params: { conversation_id: conversation.id },
});

const contactRoute = conversation => ({
  name: 'contacts_edit',
  params: { contactId: conversation.contactId },
});
</script>

<template>
  <div
    class="overflow-hidden rounded-xl outline outline-1 outline-n-container bg-n-solid-2"
  >
    <h3 class="px-4 py-3 text-sm font-medium text-n-slate-12">
      <MetricHint :label="title" :tooltip="tooltip" />
    </h3>
    <p v-if="showsLatestOnly" class="px-4 pb-2 text-xs text-n-slate-11">
      {{ $t('COMPANY_REPORTS.SHOWING_LATEST', { count: rows.length }) }}
    </p>
    <div class="overflow-x-auto">
      <table class="w-full min-w-0 text-sm">
        <thead class="text-left text-n-slate-11">
          <tr>
            <th class="px-4 py-2 font-medium">
              {{ $t('COMPANY_REPORTS.METRICS.CONVERSATION') }}
            </th>
            <th class="px-4 py-2 font-medium">
              {{ $t('COMPANY_REPORTS.METRICS.CONTACT') }}
            </th>
            <th class="px-4 py-2 font-medium">
              {{ $t('COMPANY_REPORTS.METRICS.AGENTS') }}
            </th>
            <th class="px-4 py-2 font-medium">
              {{ $t('COMPANY_REPORTS.METRICS.LAST_ACTIVITY') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!rows.length">
            <td
              colspan="4"
              class="px-4 py-6 text-sm text-center text-n-slate-11"
            >
              {{ emptyLabel }}
            </td>
          </tr>
          <tr
            v-for="conversation in rows"
            :key="conversation.id"
            class="border-t border-n-weak"
          >
            <td class="px-4 py-2">
              <router-link
                :to="conversationRoute(conversation)"
                target="_blank"
                rel="noopener noreferrer"
                class="text-n-slate-12 hover:underline"
              >
                {{ `#${conversation.id}` }}
              </router-link>
            </td>
            <td class="px-4 py-2 break-words">
              <router-link
                v-if="conversation.contactId"
                :to="contactRoute(conversation)"
                target="_blank"
                rel="noopener noreferrer"
                class="text-n-slate-12 hover:underline"
              >
                {{ conversation.contactName }}
              </router-link>
              <span v-else class="text-n-slate-12">
                {{ conversation.contactName || emptyValue }}
              </span>
            </td>
            <td class="px-4 py-2 break-words text-n-slate-12">
              {{
                conversation.assigneeName ||
                $t('COMPANY_REPORTS.UNASSIGNED_AGENT')
              }}
            </td>
            <td class="px-4 py-2 text-n-slate-12">
              {{ formatTimestamp(conversation.lastActivityAt) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
